import type { Page } from '../components/PageConfigProvider/types';
import type { SecondaryDimensionValues } from '../components/PageConfigProvider/types/dimension';
import {
  type DimensionSource,
  DimensionType,
  type PlotOptions,
  type PositionedItem,
} from '../components/PageConfigProvider/types/item';
import { FilterId, type PrimaryFilterId } from '../filters/types/filters';

type LegacyPlot = PlotOptions & { primaryEntitySwitcher?: boolean };

// Backend format for dimensionSource
type BackendDimensionSource = {
  type?: string;
  segment?: SecondaryDimensionValues;
};

export const deserializeItemDimensionSources = (
  item: PositionedItem
): PositionedItem => {
  if (!item.plot) return item;

  const { primaryEntitySwitcher, ...rest }: LegacyPlot = item.plot;
  const plot = {
    ...rest,
    // TODO: remove once the backfill renames the saved field.
    ...(primaryEntitySwitcher !== undefined &&
      rest.isSingleEntity === undefined && {
        isSingleEntity: primaryEntitySwitcher,
      }),
  };

  // Deserialize dimensionSource
  if (plot.dimensionSource && typeof plot.dimensionSource === 'object') {
    plot.dimensionSource = deserializeDimensionSource(
      plot.dimensionSource as unknown as BackendDimensionSource
    );
  }

  return { ...item, plot };
};

// Transform a Page object: deserialize dimensionSource from backend format to frontend format
export const deserializePageDimensionSources = (page: Page): Page => {
  return {
    ...page,
    tabs: page.tabs.map((tab) => ({
      ...tab,
      tabContent: {
        ...tab.tabContent,
        items:
          tab.tabContent?.items?.map(deserializeItemDimensionSources) ?? [],
      },
    })),
  };
};

const deserializeDimensionSource = (
  backend: BackendDimensionSource | undefined
): DimensionSource | undefined => {
  if (!backend) return undefined;
  if (backend.type === DimensionType.PRIMARY_DIMENSION) {
    return 'primary';
  }
  if (backend.type === DimensionType.TOP_SEGMENTS && backend.segment) {
    return backend.segment;
  }
  return undefined;
};

export const dimensionSourceToFilterId = (
  dimensionSource: SecondaryDimensionValues | string
): PrimaryFilterId | undefined => {
  const upperDimensionSource = dimensionSource.toUpperCase();
  if (upperDimensionSource === 'COMPANY') return FilterId.COMPANY;
  if (upperDimensionSource === 'GEOGRAPHY') return FilterId.GEOGRAPHY;
  if (upperDimensionSource === 'ROLE') return FilterId.ROLE;
  return undefined;
};
